import { useState, useEffect, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
    Table,
    TableHeader,
    TableRow,
    TableHead,
    TableBody,
    TableCell,
} from "@/components/ui/table.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Switch } from "@/components/ui/switch.tsx"
import { Label } from "@/components/ui/label.tsx"
import {
    Plus,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    UserCheck,
    Loader2,
} from "lucide-react"
import { getCandidates, enableCandidateLogin } from "@/api/candidates"

function highlightMatch(text, query) {
    if (!text) return ""
    if (!query) return text
    const regex = new RegExp(`(${query})`, "ig")
    const parts = text.split(regex)
    return parts.map((part, i) =>
        regex.test(part) ? <strong key={i}>{part}</strong> : part
    )
}

export default function CandidatesPage() {
    const [candidates, setCandidates] = useState([])
    const [search, setSearch] = useState("")
    const [sortField, setSortField] = useState("firstName")
    const [direction, setDirection] = useState("asc")
    const [page, setPage] = useState(1)
    const [showAll, setShowAll] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    const pageSize = 15
    const navigate = useNavigate()

    useEffect(() => {
        const fetchCandidates = async () => {
            setIsLoading(true)
            const data = await getCandidates({ show_all: showAll })
            if (Array.isArray(data)) setCandidates(data)
            setIsLoading(false)
        }
        fetchCandidates()
    }, [showAll])

    const filteredCandidates = useMemo(() => {
        const terms = search.toLowerCase().split(/\s+/).filter(Boolean)
        return candidates.filter((c) => {
            const name = `${c.firstName} ${c.lastName}`.toLowerCase()
            const email = c.email?.toLowerCase() || ""
            return terms.every(term => name.includes(term) || email.includes(term))
        })
    }, [search, candidates])

    const sortedCandidates = useMemo(() => {
        const sorted = [...filteredCandidates]
        sorted.sort((a, b) => {
            const valA = a[sortField]?.toString().toLowerCase() || ""
            const valB = b[sortField]?.toString().toLowerCase() || ""
            if (valA < valB) return direction === "asc" ? -1 : 1
            if (valA > valB) return direction === "asc" ? 1 : -1
            return 0
        })
        return sorted
    }, [filteredCandidates, sortField, direction])

    const paginatedCandidates = useMemo(() => {
        const startIndex = (page - 1) * pageSize
        return sortedCandidates.slice(startIndex, startIndex + pageSize)
    }, [sortedCandidates, page])

    const totalPages = Math.ceil(sortedCandidates.length / pageSize)

    const toggleSort = (field) => {
        if (sortField === field) {
            setDirection(direction === "asc" ? "desc" : "asc")
        } else {
            setSortField(field)
            setDirection("asc")
        }
    }

    const handleEnableLogin = async (candidateId) => {
        try {
            setIsLoading(true)
            await enableCandidateLogin(candidateId)
            // Refetch candidates to update the list
            const data = await getCandidates({ show_all: showAll })
            if (Array.isArray(data)) setCandidates(data)
        } catch (error) {
            console.error("Failed to enable login:", error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
                <h2 className="text-2xl font-bold text-[#8C3161]">Candidates</h2>

                <Button
                    onClick={() => navigate("/admin/candidate/new")}
                    className="bg-[#8C3161] hover:bg-[#73274f] text-white flex items-center space-x-2 w-full md:w-auto"
                >
                    <Plus className="w-5 h-5 mr-2" /> Add Candidate
                </Button>
            </div>

            <div className="flex flex-col gap-4 md:flex-row md:items-center mb-6">
                <Input
                    type="text"
                    placeholder="Search by name or email"
                    value={search}
                    onChange={(e) => {
                        setPage(1)
                        setSearch(e.target.value)
                    }}
                    className="w-full md:w-80"
                />

                <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4 md:min-w-fit">
                    <div className="flex items-center space-x-2 whitespace-nowrap">
                        <Switch
                            id="show-all"
                            checked={showAll}
                            onCheckedChange={setShowAll}
                        />
                        <Label htmlFor="show-all" className="cursor-pointer">
                            Show all candidates
                        </Label>
                    </div>
                    <span className="text-sm text-muted-foreground whitespace-nowrap min-w-[280px]">
                        {showAll ? "(Displaying all candidates)" : "(Displaying candidates with login enabled)"}
                    </span>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center py-20 animate-in fade-in duration-300">
                    <Loader2 className="h-8 w-8 animate-spin text-[#8C3161]" />
                </div>
            ) : (
                <div className="animate-in fade-in duration-300">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead onClick={() => toggleSort("firstName")} className="cursor-pointer">
                                    Name {sortField === "firstName" ? (direction === "asc" ? <ArrowUp className="inline h-4 w-4" /> : <ArrowDown className="inline h-4 w-4" />) : <ArrowUpDown className="inline h-4 w-4 text-muted" />}
                                </TableHead>
                                <TableHead>Known As</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Phone Number</TableHead>
                                <TableHead>Age</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead onClick={() => toggleSort("contact_date")} className="cursor-pointer">
                                    Date Added {sortField === "contact_date" ? (direction === "asc" ? <ArrowUp className="inline h-4 w-4" /> : <ArrowDown className="inline h-4 w-4" />) : <ArrowUpDown className="inline h-4 w-4 text-muted" />}
                                </TableHead>
                                {showAll && <TableHead>Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedCandidates.map((candidate) => {
                                const fullName = `${candidate.firstName} ${candidate.lastName}`
                                const email = candidate.email
                                const dateAdded = candidate.contact_date || candidate.created_at
                                const formattedDate = dateAdded
                                    ? new Date(dateAdded).toLocaleDateString("en-GB", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })
                                    : "N/A"

                                return (
                                    <TableRow key={candidate.slugId}>
                                        <TableCell>
                                            <Link
                                                to={`/admin/candidate/${candidate.slugId}`}
                                                className="text-[#5D1F42] hover:underline hover:text-[#8C3161] transition"
                                            >
                                                {highlightMatch(fullName, search)}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{candidate.known_as || "—"}</TableCell>
                                        <TableCell>{highlightMatch(email, search)}</TableCell>
                                        <TableCell>{candidate.phone_number || "—"}</TableCell>
                                        <TableCell>
                                            {candidate.dob
                                                ? new Date().getFullYear() - new Date(candidate.dob).getFullYear()
                                                : "—"}
                                        </TableCell>
                                        <TableCell>{candidate.status}</TableCell>
                                        <TableCell>{formattedDate}</TableCell>
                                        {showAll && (
                                            <TableCell>
                                                {!candidate.loginEnabled && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleEnableLogin(candidate.slugId)}
                                                        className="text-[#8C3161] border-[#8C3161] hover:bg-[#8C3161] hover:text-white"
                                                    >
                                                        <UserCheck className="w-4 h-4 mr-1" />
                                                        Enable Login
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}

            <div className="flex items-center justify-between mt-4">
                <Button onClick={() => setPage(page - 1)} disabled={page === 1} variant="outline">
                    Previous
                </Button>
                <span>Page {page} of {totalPages}</span>
                <Button onClick={() => setPage(page + 1)} disabled={page === totalPages} variant="outline">
                    Next
                </Button>
            </div>
        </div>
    )
}
